import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/SimSummaryTable.css";

const money = (v) =>
  v == null ? "" : Number(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getTodayDate = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toDateTimePayload = (dateValue) => `${dateValue}T00:00:00`;

const isEmptyMetricValue = (value) =>
  value === null || value === undefined || value === "";

const GAME_CODE_SHORTFORM_MAP = {
  prosperoustiger: "pt",
  mahjongways: "mj",
  dragonmahjong:"dmj",
  treasuresofaztec:"toa",
  kingofzeus:"koz",
  wildbandito:"wb",
  mahjongways2:"mj2",
  houseofolympus:"hoo",
  superace:"sa",
  journeytowealth:"jtw",
  pinatastack:"ps",
};

const toDisplayGameCode = (code) => {
  const rawCode = String(code || "").trim();

  if (!rawCode) {
    return code;
  }

  const [baseGameName, ...suffixParts] = rawCode.split("-");
  const mappedBase =
    GAME_CODE_SHORTFORM_MAP[String(baseGameName || "").toLowerCase()] || baseGameName;

  return suffixParts.length > 0 ? `${mappedBase}-${suffixParts.join("-")}` : mappedBase;
};

const getGroupRowClassName = (groupname) => {
  const normalizedGroupName = String(groupname ?? "").trim();

  if (normalizedGroupName === "1") return "group-row-1";
  if (normalizedGroupName === "2") return "group-row-2";
  if (normalizedGroupName === "3") return "group-row-3";
  if (normalizedGroupName === "4") return "group-row-4";
  if (normalizedGroupName === "5") return "group-row-5";
  if (normalizedGroupName === "6") return "group-row-6";
  if (normalizedGroupName === "7") return "group-row-7";
  if (normalizedGroupName === "8") return "group-row-8";
  if (normalizedGroupName === "9") return "group-row-9";
  if (normalizedGroupName === "10") return "group-row-10";
  if (normalizedGroupName === "11") return "group-row-11";
  if (normalizedGroupName === "12") return "group-row-12";

  return "";
};

export default function SimSummaryTable({ data = [] }) {
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [selectedMetric, setSelectedMetric] = useState("pnl");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState(Array.isArray(data) ? data : []);

  const fetchSummary = (dateValue) => {
    setLoading(true);
    setError("");

    axios
      .post(process.env.REACT_APP_BACKEND_ADDRESS + "/rpt/getsimsummary", {
        createdat: toDateTimePayload(dateValue),
      })
      .then((response) => {
        setLoading(false);

        if (response?.data?.description === "ok") {
          setRows(response?.data?.data || []);
          return;
        }

        setRows([]);
        setError(response?.data?.description || "Failed to fetch summary");
      })
      .catch((requestError) => {
        setLoading(false);
        setRows([]);
        setError(
          requestError?.response?.data?.description ||
            requestError?.message ||
            "Failed to fetch summary"
        );
      });
  };

  useEffect(() => {
    fetchSummary(getTodayDate());
  }, []);

  const getGameMetricValue = (game) => {
    if (!game) {
      return 0;
    }

    if (selectedMetric === "totalRound") {
      return Number(game?.roundCount) || 0;
    }

    if (selectedMetric === "turnOver") {
      return Number(game?.totalTurnover) || 0;
    }

    return Number(game?.profitLoss) || 0;
  };

  const getGameMetricDisplayValue = (game) => {
    if (!game) {
      return null;
    }

    if (selectedMetric === "totalRound") {
      return game?.roundCount;
    }

    if (selectedMetric === "turnOver") {
      return game?.totalTurnover;
    }

    return game?.profitLoss;
  };

  const getRowMetricValue = (row) => {
    const games = row?.games || [];

    if (selectedMetric === "pnl") {
      if (row?.totalPL !== undefined && row?.totalPL !== null) {
        return Number(row.totalPL) || 0;
      }
      return games.reduce((sum, game) => sum + (Number(game?.profitLoss) || 0), 0);
    }

    if (selectedMetric === "totalRound") {
      return games.reduce((sum, game) => sum + (Number(game?.roundCount) || 0), 0);
    }

    return games.reduce((sum, game) => sum + (Number(game?.totalTurnover) || 0), 0);
  };

  const formatMetricValue = (value) => {
    if (selectedMetric === "totalRound") {
      if (isEmptyMetricValue(value)) {
        return "";
      }

      return (Number(value) || 0).toLocaleString("en-US", {
        maximumFractionDigits: 0,
      });
    }

    if (selectedMetric === "turnOver" && isEmptyMetricValue(value)) {
      return "";
    }

    return money(value);
  };

  const metricHeaderLabel =
    selectedMetric === "pnl"
      ? "totalPL"
      : selectedMetric === "totalRound"
      ? "Total Round"
      : "Turn Over";

  // 1. collect unique game codes for columns
  const gameCodes = [
    ...new Set(rows.flatMap((row) => (row.games || []).map((g) => g.gameCode))),
  ].sort((a, b) => String(a).localeCompare(String(b)));

  // 2. column total per game
  const gameTotals = gameCodes.reduce((acc, code) => {
    acc[code] = rows.reduce((sum, row) => {
      const game = (row.games || []).find((g) => g.gameCode === code);
      return sum + getGameMetricValue(game);
    }, 0);
    return acc;
  }, {});

  const grandTotal = rows.reduce((sum, row) => sum + getRowMetricValue(row), 0);
  const totalEndBalance = rows.reduce((sum, row) => sum + (Number(row?.endbalance) || 0), 0);

  return (
    <div>
      <div style={{ marginBottom: "12px" }}>
        <div>
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
          />
          <input
            type="button"
            value={loading ? "Loading..." : "Search"}
            disabled={loading}
            onClick={() => fetchSummary(selectedDate)}
            style={{ marginLeft: "8px" }}
          />
        </div>
        <div style={{ marginTop: "8px" }}>
          <label>
            <input
              type="radio"
              name="metricType"
              value="pnl"
              checked={selectedMetric === "pnl"}
              onChange={(event) => setSelectedMetric(event.target.value)}
            />
            PnL
          </label>
          <label style={{ marginLeft: "10px" }}>
            <input
              type="radio"
              name="metricType"
              value="totalRound"
              checked={selectedMetric === "totalRound"}
              onChange={(event) => setSelectedMetric(event.target.value)}
            />
            Total Round
          </label>
          <label style={{ marginLeft: "10px" }}>
            <input
              type="radio"
              name="metricType"
              value="turnOver"
              checked={selectedMetric === "turnOver"}
              onChange={(event) => setSelectedMetric(event.target.value)}
            />
            Turn Over
          </label>
        </div>
      </div>

      {error && <small style={{ color: "red" }}>{error}</small>}

      <div className="table-wrap">
        <table className="sim-table sim-summary-table">
          <thead>
            <tr>
              <th>No</th>
              <th>UserID</th>
              <th>Player ID</th>
              <th>End Balance</th>
              <th className="total-head">{metricHeaderLabel}</th>

              {gameCodes.map((code) => (
                <th key={code} className="game-head" style={{ fontWeight: "bold" }}>
                  {toDisplayGameCode(code)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => (
              <tr key={row.playerid} className={getGroupRowClassName(row.groupname)}>
                <td>{index + 1}</td>
                <td className="userid">{row.playername}</td>
                <td>{row.playerid}</td>
                <td>{money(row.endbalance)}</td>
                {(() => {
                  const rowValue = getRowMetricValue(row);

                  return (
                    <td
                      className={
                        selectedMetric === "pnl"
                          ? `total-cell ${rowValue < 0 ? "neg" : "pos"}`
                          : "total-cell"
                      }
                      style={
                        selectedMetric === "pnl"
                          ? undefined
                          : { color: "black", fontWeight: "bold" }
                      }
                    >
                      {formatMetricValue(rowValue)}
                    </td>
                  );
                })()}

                {gameCodes.map((code) => {
                  const game = (row.games || []).find((g) => g.gameCode === code);
                  const value = getGameMetricValue(game);
                  const displayValue = getGameMetricDisplayValue(game);

                  return (
                    <td
                      key={code}
                      className={
                        selectedMetric === "pnl"
                          ? value < 0
                            ? "neg"
                            : value > 0
                            ? "pos"
                            : ""
                          : ""
                      }
                      style={
                        selectedMetric === "pnl"
                          ? undefined
                          : { color: "black", fontWeight: "bold" }
                      }
                    >
                      {formatMetricValue(displayValue)}
                    </td>
                  );
                })}
              </tr>
            ))}

            <tr className="summary-row">
              <td></td>
              <td></td>
              <td></td>
              <td className={totalEndBalance < 0 ? "neg" : totalEndBalance > 0 ? "pos" : ""}>
                {money(totalEndBalance)}
              </td>
              <td
                className={selectedMetric === "pnl" ? (grandTotal < 0 ? "neg" : "pos") : ""}
                style={
                  selectedMetric === "pnl"
                    ? undefined
                    : { color: "black", fontWeight: "bold" }
                }
              >
                {formatMetricValue(grandTotal)}
              </td>

              {gameCodes.map((code) => (
                <td
                  key={code}
                  className={
                    selectedMetric === "pnl"
                      ? gameTotals[code] < 0
                        ? "neg"
                        : "pos"
                      : ""
                  }
                  style={
                    selectedMetric === "pnl"
                      ? undefined
                      : { color: "black", fontWeight: "bold" }
                  }
                >
                  {formatMetricValue(gameTotals[code])}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}